import {FeatureAndUiState} from "../services/features-api/models";
import {Icons} from "../utility/icons";
import {ISavedFeatureService} from "../services/features-api/saved_features";
import {IFeatureState} from "../services/feature_state";
import {LTServices} from "../lt-ng/lt-services";
import {ICoordinateAdjustmentService} from "../services/coordinate-adjustment";
import {IReportService} from "../services/features-api/report_service";
import {IToastService, TOAST_DURATION_LONG} from "../services/ToastService";
import IMapService = LTServices.IMapService;
import {Point} from "ol/geom";
import {_t, sprintf} from "../utility/i18n";
import {PinReport, ReportKind} from "../services/ISharedFeatureState";

interface ISavedFeaturesSope extends ng.IScope {
    hoverFeature: (f: FeatureAndUiState, enterOrExit: boolean) => void;
    deleteReport: (report: any) => void;
    reportsBeingDeleted: Set<string>;
    fragReportsForFeature: (userFeature: FeatureAndUiState) => object[];
    featureClickEventData: (feature: FeatureAndUiState) => any;
    showSavedReport(report: any);
    customFeaturesIsLoading(): boolean;
    predefinedFeaturesIsLoading(): boolean;

    // A user's saved custom features
    customFeatures(): FeatureAndUiState[];
    // A user's predefined features that have been selected from context layers and saved
    predefinedFeatures(): FeatureAndUiState[];
    // the icon that goes with a feature
    featureIcon(userFeature: FeatureAndUiState) : string;
    // turn the feature on/off on the map - the 'eye' button
    toggleVisible(userFeature: FeatureAndUiState);
    // delete the feature "permanently"
    deleteFeature(userFeature: FeatureAndUiState);
    // show the feature in the 'Feature Report' pane
    showFeatureReport(feature: FeatureAndUiState);
    // is the feature the one that is shown in the features pane right now
    isFeatureSelected(feature: FeatureAndUiState);

    getReportEvent(report: any, action: string): any;
}

export class SavedFeaturesCtrl {
    public static $inject = [
        '$scope',
        'savedFeatureService',
        'featureState',
        'landcareMapService',
        'coordinateAdjustmentService',
        'reportService',
        'toastService'
    ];

    constructor(protected $scope: ISavedFeaturesSope,
                protected savedFeatureService: ISavedFeatureService,
                protected featureState: IFeatureState,
                protected mapService: IMapService,
                protected coordinateAdjustmentService: ICoordinateAdjustmentService,
                protected reportService: IReportService,
                protected toastService: IToastService) {

        /*let featureReports : { [key: string] : object[]} = {};
        let reportsRequest = reportService.getReports();
        reportsRequest.then((reports) => {
            reports.forEach((rp: any) => {
                if (!rp) return;
                let subject = rp.subject;
                if (subject && subject.id) {
                    featureReports[subject.id] = featureReports[subject.id] || [];
                    featureReports[subject.id].push(rp);
                }
            });
        });*/

        $scope.fragReportsForFeature = (userFeature: FeatureAndUiState) => {
            const id = userFeature && userFeature.userFeature.id;
            return this.reportService.reportsForUserFeatureId(id);
        };



        $scope.showSavedReport = (report: any) => {
            if (report && report.id) {
                $scope.$emit('showFragReport', report.id);
            }
        };

        $scope.getReportEvent = (report, action = 'show') => {
            return {"category":"report", "action":action, "label": report.kind };
        };

        const deleting = new Set<string>();

        $scope.reportsBeingDeleted = deleting;

        $scope.deleteReport = (report: any) => {
            const toastMessage = sprintf(
                _t('Features.DELETING_REPORT', 'Deleting report %s'),
                report.title || report.subject.title
            );

            deleting.add(report.id);

            toastService.showToast(
                toastMessage,
                TOAST_DURATION_LONG,
                null,
                {
                    onClick() {

                    },
                    label: 'Cancel'
                }).then(result => {
                    if (!result.actionClicked) {
                        reportService.deleteReport(report)
                            .then(() => {
                                this.toastService.showToast(_t('Features.REPORT_DELETED', 'Report deleted'));
                            }, () => {
                                this.toastService.showToast(_t('Features.ERROR_DELETING_REPORT', 'Error deleting report'));
                            })
                    }
            }).finally(() => {
                deleting.delete(report.id);
            })
        };

        $scope.hoverFeature = (f: FeatureAndUiState, enterOrExit: boolean) => {
            const maybePin = f && f.mapFeatures && f.mapFeatures[0];

            if (maybePin && maybePin.getGeometry() instanceof Point) {
                maybePin.set('hover', enterOrExit);
            }

        };

        $scope.predefinedFeaturesIsLoading = () => this.savedFeatureService.predefinedFeatures.loading;
        $scope.customFeaturesIsLoading = () => this.savedFeatureService.customFeatures.loading;

        $scope.customFeatures = () => this.savedFeatureService.customFeatures.features;
        $scope.predefinedFeatures = () => this.savedFeatureService.predefinedFeatures.features;

        $scope.isFeatureSelected = (feature: FeatureAndUiState) => {
            const rp = this.featureState.reportIfSingle();
            if (rp) {
                return rp.userFeatures == feature;
            }
            const mrp  = this.featureState.getActiveReportIfMultiplePins();
            return mrp && mrp.reports.some(r => r.userFeatures === feature);
        };

        $scope.featureIcon = (f) => {
            switch (f.userFeature.geometryType) {
                case 'Point':
                    return Icons.Pin;
                case 'MultiPolygon':
                    return Icons.Polygon;
                default:
                    return Icons.Polygon;
            }
        };

        const updateFeatureVisibility = (uf: FeatureAndUiState) => {
            (uf.mapFeatures || [])
                .forEach((mf) => mf.set('hidden', !uf.visible));
        };

        $scope.toggleVisible = (uf) => {
            uf.visible = !uf.visible;
            updateFeatureVisibility(uf);
        };

        $scope.deleteFeature = (f) => {
            this.savedFeatureService.deleteFeature(f).then(() => {

            }, () => {
                this.toastService.showToast(_t('Features.ERROR_DELETING_FEATURE', `'Error deleting feature'`));
                f.deleting = false;
            });
        };

        $scope.showFeatureReport = (feature: FeatureAndUiState) => {
            const activeReport = this.featureState.reportIfSingle();
            if (activeReport && activeReport.userFeatures == feature) {
                // we're already showing the report for this feature, do nothing;
                return;
            }

            feature.visible = true;
            updateFeatureVisibility(feature);
            this.featureState.showReportForFeature(feature);

            const view = this.mapService.map.getView();
            const rp = this.featureState.sharedState().activeReport;

            const zoomToPinReport = (rp: PinReport) => {
                const adjustedCoord = this.coordinateAdjustmentService.adjustCentreForScreenSpace(
                    rp.point.getGeometry().getCoordinates(),
                    this.mapService.map,
                    view.getZoom());

                view.setCenter(adjustedCoord);
            };

            if (rp.kind == ReportKind.area) {
                view.fit(rp.geometry, {
                    padding: this.coordinateAdjustmentService.padding([5, 5, 5, 5]),
                    size: this.mapService.map.getSize()
                });
            } else if (rp.kind === ReportKind.pin/* && rp.pinData*/) {
                zoomToPinReport(rp);
            } else if (rp.kind === ReportKind.multiplePin) {
                const pinRp = rp.reports.find(r => r.userFeatures == feature);
                zoomToPinReport(pinRp || rp.reports[0]);
            }
        };

        $scope.featureClickEventData = (feature: FeatureAndUiState) => {
            try {
                const uf = feature.userFeature;
                if (uf.geometryType == 'Point') {
                    const geoJsonFeatures = uf.featureCollection.features;
                    const point = geoJsonFeatures[0].geometry;
                    let x = undefined;
                    let y = undefined;
                    if (point.type == 'Point') {
                        x = point.coordinates[0];
                        y = point.coordinates[1];
                    }
                    return {
                        "category":"SELECT_PIN",
                        "action":"",
                        "label":`Pin ${uf.id}: ${x} ${y}`
                    };
                }

                return {
                    "category":"SELECT_FEATURE",
                    "action":"",
                    "label":`Feature ${uf.id} ${uf.geometryType} ${uf.kind}`
                };

            } catch (e) { }

            return {
                "category":"SELECT_FEATURE",
                "action":"",
                "label": `Feature unknown`
            };
        }
    }
}
